import * as Turf from '@turf/turf';
import type { FeatureCollection } from 'geojson';
import type { WatchSource } from 'vue';
import { ref } from 'vue';

import type { BBox } from '@nora-monorepo/shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { OperationBboxStateManager } from '#nora-ui/map/map/state-managers/OperationBboxStateManager';
import type { FileStateSnapshot, Persistable } from '#nora-ui/map/types/persistence.types';
import { useFilesApiStore } from '#nora-ui/stores/api/files-api-store';

export interface SelectedOverlayFile {
  id: string;
  domain: string;
  domainId: string;
  path: string;
}

export class FileStateManager extends OperationBboxStateManager<FeatureCollection> implements Persistable<FileStateSnapshot> {
  private selectedFileRef = ref<SelectedOverlayFile | null>(null);
  private fileLayerVisibleRef = ref(true);

  get selectedFile(): SelectedOverlayFile | null {
    return this.selectedFileRef.value;
  }

  get fileLayerVisible(): boolean {
    return this.fileLayerVisibleRef.value;
  }

  /**
   * The fetched files GeoJSON collections, one per shown operation.
   * Populated by load()/loadOperation() and used by the layer manager for rendering.
   */
  get filesGeoJsonCollections(): FeatureCollection[] {
    return [...this.dataByOperation.values()];
  }

  constructor(map: NoraMap) {
    super(map);
  }

  setSelectedFile(file: SelectedOverlayFile): void {
    this.selectedFileRef.value = file;
  }

  clearSelectedFile(): void {
    this.selectedFileRef.value = null;

    this.map.layers.file.clearSelectedFile();
  }

  setVisibility(isVisible: boolean): void {
    const wasVisible = this.fileLayerVisibleRef.value;

    this.fileLayerVisibleRef.value = isVisible;
    this.map.layers.file.setVisibility(isVisible);

    if (isVisible && !wasVisible) {
      void this.loadCurrentBbox();
    }
  }

  toggleVisibility(): void {
    this.setVisibility(!this.fileLayerVisibleRef.value);
  }

  protected override async fetchOperationData(bbox: BBox, operationId: string): Promise<FeatureCollection> {
    const filesApiStore = useFilesApiStore();
    const polygon = Turf.bboxPolygon(bbox.getGeoJsonBBox());
    const filesGeoJson = await filesApiStore.fetchFilesGeoJson(polygon, operationId);

    return filesGeoJson;
  }

  protected override refreshLayer(): void {
    this.map.layers.file.load();
  }

  /**
   * Serialize the file state to a plain object for persistence.
   */
  serialize(): FileStateSnapshot {
    return {
      layerVisible: this.fileLayerVisibleRef.value,
      selectedFile: this.selectedFileRef.value,
    };
  }

  /**
   * Restore file state from a persisted snapshot.
   */
  deserialize(snapshot: FileStateSnapshot): void {
    this.fileLayerVisibleRef.value = snapshot.layerVisible;
    this.selectedFileRef.value = snapshot.selectedFile;

    // Apply visibility to the layer
    this.map.layers.file.setVisibility(snapshot.layerVisible);
  }

  /**
   * Returns a watch source for triggering persistence when state changes.
   */
  getWatchSource(): WatchSource {
    return () => [
      this.fileLayerVisibleRef.value,
      this.selectedFileRef.value,
    ];
  }

  /**
   * Load file state by fetching files for every shown operation based on the current map bbox.
   */
  async load(): Promise<void> {
    this.unload();

    await this.loadCurrentBbox();
  }

  override async loadCurrentBbox(): Promise<void> {
    if (!this.fileLayerVisibleRef.value) {
      return;
    }

    await super.loadCurrentBbox();
  }

  override async loadOperation(operationId: string): Promise<void> {
    if (!this.fileLayerVisibleRef.value) {
      return;
    }
    await super.loadOperation(operationId);
  }

  /**
   * Unload/clear all file state.
   */
  unload(): void {
    this.selectedFileRef.value = null;
    this.fileLayerVisibleRef.value = true;
    this.clearOperationData();
    this.map.layers.file.unload();
  }
}
